import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import { supabase } from '../../services/supabase'
import {
  CATEGORIES,
  OBJECTIVES,
  REPORT_TYPES,
  SAMPLE_REPORTS,
  SOURCING,
  normalizeWebsite,
  parseStakeholderEmails,
  subcategoriesFor,
} from './riskIntakeTaxonomy'
import { EsBadge } from './ui'

const field =
  'w-full rounded-lg border border-es-hairline bg-es-white px-3 py-2 text-sm text-es-navy focus:outline-none focus:ring-2 focus:ring-es-signal/40'

const MAX_SUPPLIERS = 10

type SupplierDraft = {
  supplierName: string
  supplierWebsite: string
  category: string
  subCategories: string[]
  brand: string
  sourcingCountry: string
  supplierAddress: string
  sourcingModel: string
  reportTypes: string[]
}

function emptySupplier(): SupplierDraft {
  return {
    supplierName: '',
    supplierWebsite: '',
    category: CATEGORIES[0] || 'Chemicals',
    subCategories: [],
    brand: '',
    sourcingCountry: '',
    supplierAddress: '',
    sourcingModel: SOURCING[0],
    reportTypes: [REPORT_TYPES[0]],
  }
}

type QueueLine = {
  id: string
  request_id: string
  supplier_name: string
  report_types: string[] | null
  line_status: string
  category: string
  rejection_reason?: string | null
  created_at?: string
}

export function SupplierRiskIntakeView() {
  const { user, effectiveCompanyId: authEffectiveCompanyId, isAdmin } = useAuth()
  const { company, settings } = useCompany()
  const cfg = (settings?.customizations as any)?.supplierRiskIntake || {}
  const siteLabel = cfg.labels?.site || 'Site / buying region'
  const siteHint =
    cfg.labels?.siteHint ||
    'Location or region for which the company is buying (e.g. plant / market served).'
  const enabled = cfg.enabled !== false || isAdmin

  const [queue, setQueue] = useState<QueueLine[]>([])
  const [requesterName, setRequesterName] = useState('')
  const [site, setSite] = useState('')
  const [listOfUsers, setListOfUsers] = useState('')
  const [endObjective, setEndObjective] = useState<string>(OBJECTIVES[0])
  const [endObjectiveOther, setEndObjectiveOther] = useState('')
  const [businessContext, setBusinessContext] = useState('')
  const [suppliers, setSuppliers] = useState<SupplierDraft[]>([emptySupplier()])
  const [busy, setBusy] = useState(false)
  const [queueBusy, setQueueBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [done, setDone] = useState<{ id: string; status: string; supplierCount: number } | null>(null)

  // Prefer View-as company so Simple admins acting for Kenvue hit Kenvue TPRM settings
  const effectiveCompanyId = authEffectiveCompanyId || company?.id || null

  async function loadQueue() {
    if (!isAdmin || !effectiveCompanyId) return
    const { data } = await supabase
      .from('supplier_risk_intake_suppliers')
      .select(
        'id, request_id, supplier_name, report_types, line_status, category, rejection_reason, created_at',
      )
      .eq('company_id', effectiveCompanyId)
      .order('created_at', { ascending: false })
      .limit(40)
    setQueue((data as QueueLine[]) || [])
  }

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, effectiveCompanyId, done])

  function updateSupplier(idx: number, patch: Partial<SupplierDraft>) {
    setSuppliers((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }

  function toggleReportType(idx: number, type: string) {
    const cur = suppliers[idx].reportTypes
    const next = cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type]
    updateSupplier(idx, { reportTypes: next.length ? next : cur })
  }

  function toggleSubCategory(idx: number, sub: string) {
    const cur = suppliers[idx].subCategories
    const next = cur.includes(sub) ? cur.filter((s) => s !== sub) : [...cur, sub]
    updateSupplier(idx, { subCategories: next })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!effectiveCompanyId) {
      setError('No company context. Sign in with a company account.')
      return
    }
    if (!requesterName.trim()) {
      setError('Your name is required.')
      return
    }
    const emails = parseStakeholderEmails(listOfUsers)
    if (emails.error) {
      setError(emails.error)
      return
    }
    if (!businessContext.trim()) {
      setError('Business Context and Justification is required.')
      return
    }
    if (endObjective === 'Anything else' && !endObjectiveOther.trim()) {
      setError('Describe objective is required when End Objective is Anything else.')
      return
    }
    if (!suppliers.length) {
      setError('Add at least one supplier.')
      return
    }
    for (let i = 0; i < suppliers.length; i++) {
      const s = suppliers[i]
      if (!s.supplierName.trim()) {
        setError(`Supplier ${i + 1}: name is required.`)
        return
      }
      const web = normalizeWebsite(s.supplierWebsite)
      if (web.error) {
        setError(`Supplier ${i + 1}: ${web.error}`)
        return
      }
      if (!s.reportTypes.length) {
        setError(`Supplier ${i + 1}: select at least one report type.`)
        return
      }
    }

    setBusy(true)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('submit-supplier-risk-intake', {
        body: {
          companyId: effectiveCompanyId,
          requesterName: requesterName.trim(),
          site,
          listOfUsers: emails.emails.join(', '),
          endObjective,
          endObjectiveOther,
          additionalContext: businessContext.trim(),
          suppliers: suppliers.map((s) => ({
            supplierName: s.supplierName.trim(),
            supplierWebsite: s.supplierWebsite.trim(),
            category: s.category,
            subCategories: s.subCategories,
            brand: s.brand.trim() || undefined,
            sourcingCountry: s.sourcingCountry.trim() || undefined,
            supplierAddress: s.supplierAddress.trim() || undefined,
            sourcingModel: s.sourcingModel,
            reportTypes: s.reportTypes,
          })),
        },
      })
      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)
      setDone({
        id: data.id,
        status: data.status,
        supplierCount: data.supplierCount || suppliers.length,
      })
    } catch (err: any) {
      setError(err.message || 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  async function decideLine(lineId: string, action: 'approve' | 'reject') {
    setQueueBusy(lineId)
    setError('')
    try {
      let rejectionReason: string | undefined
      if (action === 'reject') {
        const reason = window.prompt('Reason for rejection (required):')
        if (reason == null) {
          setQueueBusy(null)
          return
        }
        rejectionReason = reason.trim()
        if (!rejectionReason) {
          setError('Rejection reason is required.')
          setQueueBusy(null)
          return
        }
      }
      const { data, error: fnErr } = await supabase.functions.invoke('supplier-risk-intake-approve', {
        body: { lineId, action, json: true, rejectionReason },
      })
      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)
      await loadQueue()
    } catch (err: any) {
      setError(err.message || `${action} failed`)
    } finally {
      setQueueBusy(null)
    }
  }

  const pendingCount = useMemo(
    () => queue.filter((q) => q.line_status === 'pending_approval').length,
    [queue],
  )

  if (!enabled) {
    return (
      <div className="rounded-lg border border-es-hairline bg-es-white p-6 text-sm text-es-muted">
        Supplier risk assessment intake is not enabled for this company.
      </div>
    )
  }

  if (done) {
    return (
      <div className="rounded-lg border border-es-hairline bg-es-white p-6 space-y-3">
        <EsBadge tone="quiet">Submitted</EsBadge>
        <h2 className="font-esDisplay text-xl text-es-navy">Request received</h2>
        <p className="text-sm text-es-muted">
          Submitted <strong className="text-es-navy">{done.supplierCount}</strong> supplier
          {done.supplierCount === 1 ? '' : 's'} (batch {done.id}). Status:{' '}
          <strong className="text-es-navy">{done.status}</strong>. TPRM governance will approve or
          reject each supplier separately; you will be emailed when a decision is made.
        </p>
        <button
          type="button"
          className="text-sm text-es-signal underline"
          onClick={() => {
            setDone(null)
            setSuppliers([emptySupplier()])
            setBusinessContext('')
            setListOfUsers('')
          }}
        >
          Submit another
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="font-esDisplay text-2xl text-es-navy">Supplier risk assessment intake</h1>
        <p className="text-sm text-es-muted mt-1">
          Submit one or more suppliers for {company?.name || 'your company'}. Each supplier can have
          different report types and is approved separately. Signed in as {user?.email}.
        </p>
      </div>

      <section className="rounded-lg border border-es-hairline bg-es-white p-5 space-y-3">
        <div>
          <h2 className="font-esDisplay text-lg text-es-navy">Sample reports</h2>
          <p className="text-sm text-es-muted mt-0.5">Download examples before you submit.</p>
        </div>
        <ul className="space-y-2">
          {SAMPLE_REPORTS.map((s) => (
            <li
              key={s.href}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-es-hairline px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium text-es-navy">{s.label}</p>
                <p className="text-xs text-es-muted truncate max-w-md">{s.reportType}</p>
              </div>
              <a
                href={s.href}
                download={s.filename}
                className="rounded-full border border-es-hairline bg-es-haze px-3 py-1.5 text-xs font-semibold text-es-navy hover:bg-es-white"
              >
                Download
              </a>
            </li>
          ))}
        </ul>
      </section>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="rounded-lg border border-es-hairline bg-es-white p-5 space-y-4">
        <label className="block text-sm">
          <span className="font-medium text-es-navy">Your name *</span>
          <input
            className={`${field} mt-1`}
            maxLength={120}
            required
            value={requesterName}
            onChange={(e) => setRequesterName(e.target.value)}
            placeholder="Full name (used in emails)"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-es-navy">{siteLabel}</span>
          <input className={`${field} mt-1`} maxLength={100} value={site} onChange={(e) => setSite(e.target.value)} />
          <span className="text-xs text-es-muted mt-1 block">{siteHint}</span>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-es-navy">List of Users *</span>
          <input
            className={`${field} mt-1`}
            maxLength={500}
            required
            value={listOfUsers}
            onChange={(e) => setListOfUsers(e.target.value)}
            placeholder="name@company.com, other@company.com"
          />
          <span className="text-xs text-es-muted mt-1 block">
            Stakeholder email IDs who need updates (comma-separated).
          </span>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-es-navy">End Objective *</span>
          <select className={`${field} mt-1`} value={endObjective} onChange={(e) => setEndObjective(e.target.value)}>
            {OBJECTIVES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        {endObjective === 'Anything else' && (
          <label className="block text-sm">
            <span className="font-medium text-es-navy">Describe objective *</span>
            <input
              className={`${field} mt-1`}
              maxLength={100}
              required
              value={endObjectiveOther}
              onChange={(e) => setEndObjectiveOther(e.target.value)}
            />
          </label>
        )}
        <label className="block text-sm">
          <span className="font-medium text-es-navy">Business Context and Justification *</span>
          <textarea
            className={`${field} mt-1`}
            maxLength={200}
            rows={3}
            required
            value={businessContext}
            onChange={(e) => setBusinessContext(e.target.value)}
          />
          <span className="text-xs text-es-muted">{businessContext.length}/200</span>
        </label>

        <div className="space-y-3 pt-2 border-t border-es-hairline">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-es-navy">Suppliers in this request *</p>
            <button
              type="button"
              className="text-xs font-semibold text-es-signal disabled:opacity-40"
              disabled={suppliers.length >= MAX_SUPPLIERS}
              onClick={() => setSuppliers((s) => [...s, emptySupplier()])}
            >
              + Add supplier
            </button>
          </div>
          <p className="text-xs text-es-muted">
            Up to {MAX_SUPPLIERS} suppliers per submission. See sample reports above. Each supplier is
            approved or rejected separately.
          </p>

          {suppliers.map((s, idx) => {
            const subs = subcategoriesFor(s.category)
            return (
              <div key={idx} className="rounded-lg border border-es-hairline bg-es-haze/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-es-muted">
                    Supplier {idx + 1}
                  </p>
                  {suppliers.length > 1 && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-700"
                      onClick={() => setSuppliers((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <fieldset className="text-sm space-y-2">
                  <legend className="font-medium text-es-navy">Report type(s) *</legend>
                  {REPORT_TYPES.map((t) => (
                    <label key={t} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={s.reportTypes.includes(t)}
                        onChange={() => toggleReportType(idx, t)}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-es-navy">Supplier Name *</span>
                    <input
                      className={`${field} mt-1`}
                      maxLength={100}
                      required
                      value={s.supplierName}
                      onChange={(e) => updateSupplier(idx, { supplierName: e.target.value })}
                    />
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-es-navy">Supplier Website *</span>
                    <input
                      className={`${field} mt-1`}
                      maxLength={200}
                      required
                      value={s.supplierWebsite}
                      onChange={(e) => updateSupplier(idx, { supplierWebsite: e.target.value })}
                      placeholder="https://supplier.com"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-es-navy">Category *</span>
                    <select
                      className={`${field} mt-1`}
                      value={s.category}
                      onChange={(e) =>
                        updateSupplier(idx, { category: e.target.value, subCategories: [] })
                      }
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-es-navy">Brand</span>
                    <input
                      className={`${field} mt-1`}
                      maxLength={100}
                      value={s.brand}
                      onChange={(e) => updateSupplier(idx, { brand: e.target.value })}
                    />
                  </label>
                </div>

                {subs.length > 0 && (
                  <fieldset className="text-sm">
                    <legend className="font-medium text-es-navy">Sub-Category (optional, multi)</legend>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1 rounded-md border border-es-hairline bg-es-white p-2">
                      {subs.map((sub) => (
                        <label key={sub} className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={s.subCategories.includes(sub)}
                            onChange={() => toggleSubCategory(idx, sub)}
                          />
                          <span>{sub}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="font-medium text-es-navy">Sourcing country</span>
                    <input
                      className={`${field} mt-1`}
                      maxLength={100}
                      value={s.sourcingCountry}
                      onChange={(e) => updateSupplier(idx, { sourcingCountry: e.target.value })}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-es-navy">Supplier Address</span>
                    <input
                      className={`${field} mt-1`}
                      maxLength={200}
                      value={s.supplierAddress}
                      onChange={(e) => updateSupplier(idx, { supplierAddress: e.target.value })}
                    />
                  </label>
                </div>

                <fieldset className="text-sm">
                  <legend className="font-medium text-es-navy">Sourcing *</legend>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {SOURCING.map((opt) => (
                      <label key={opt} className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name={`sourcing-${idx}`}
                          checked={s.sourcingModel === opt}
                          onChange={() => updateSupplier(idx, { sourcingModel: opt })}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            )
          })}
        </div>

        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-es-signal text-es-white px-5 py-2.5 text-sm font-esDisplay font-semibold disabled:opacity-50"
        >
          {busy ? 'Submitting…' : 'Submit request'}
        </button>
      </form>

      {isAdmin && (
        <div className="rounded-lg border border-es-hairline bg-es-white p-5">
          <h2 className="font-esDisplay text-lg text-es-navy mb-1">Supplier lines (admin)</h2>
          <p className="text-xs text-es-muted mb-3">
            {pendingCount} pending · Approve or reject each supplier separately
          </p>
          {queue.length === 0 ? (
            <p className="text-sm text-es-muted">No intake supplier lines yet.</p>
          ) : (
            <ul className="divide-y divide-es-hairline text-sm">
              {queue.map((r) => (
                <li key={r.id} className="py-3 flex flex-wrap gap-2 items-center justify-between">
                  <div className="min-w-0">
                    <p>
                      <strong>{r.supplier_name}</strong>
                      <span className="text-es-muted"> · {r.category}</span>
                    </p>
                    <p className="text-xs text-es-muted truncate">
                      {(r.report_types || []).join('; ') || '—'}
                    </p>
                    {r.line_status === 'rejected' && r.rejection_reason ? (
                      <p className="text-xs text-red-800 mt-1 whitespace-pre-wrap">
                        Reason: {r.rejection_reason}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <EsBadge
                      tone={
                        r.line_status === 'approved'
                          ? 'quiet'
                          : r.line_status === 'rejected'
                            ? 'alert'
                            : 'alert'
                      }
                    >
                      {r.line_status.replace(/_/g, ' ')}
                    </EsBadge>
                    {r.line_status === 'pending_approval' && (
                      <>
                        <button
                          type="button"
                          disabled={queueBusy === r.id}
                          onClick={() => decideLine(r.id, 'approve')}
                          className="rounded-full bg-es-navy text-white px-3 py-1 text-xs font-semibold disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={queueBusy === r.id}
                          onClick={() => decideLine(r.id, 'reject')}
                          className="rounded-full bg-red-800 text-white px-3 py-1 text-xs font-semibold disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
